import { useRouteOrganizationId } from '@/hooks/useRouteOrganizationId'
import {
  buildAdminQueryRoot,
  buildScopedAdminQueryKey,
  type AdminQueryKey,
  type AdminQueryRoot,
  type OrganizationIdInput,
} from '@/lib/organization-scoping'
import { keepPreviousData, queryOptions, useSuspenseQuery } from '@tanstack/react-query'
import { getRole, getRoles, type RoleOrganizationScope, type RoleScope } from './roles.api'

interface OrganizationScope {
  organizationId?: OrganizationIdInput
}

/** The key entries each fetcher reads; the scoped key builders below set them. */
type RoleIndexQueryKey = [AdminQueryRoot & RoleOrganizationScope]
type RoleShowQueryKey = [AdminQueryRoot & RoleScope]

/** Query options a caller may override; the key and fetcher stay owned by this module. */
type QueryParams<TOptions> = Partial<Omit<TOptions, 'queryKey' | 'queryFn'>>

export const roleQueryKeys = {
  all: ({ organizationId }: OrganizationScope = {}): AdminQueryKey => [
    buildAdminQueryRoot({ entity: 'role' }, { organizationId }),
  ],
  index: ({ organizationId }: OrganizationScope = {}): RoleIndexQueryKey =>
    buildScopedAdminQueryKey(roleQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'index',
    }),
  show: ({ organizationId, roleId }: RoleScope): RoleShowQueryKey =>
    buildScopedAdminQueryKey(roleQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'show',
      roleId,
    }),
}

export const roleQueries = {
  index: ({ organizationId }: OrganizationScope = {}) =>
    queryOptions({
      queryKey: roleQueryKeys.index({ organizationId }),
      queryFn: getRoles,
      placeholderData: keepPreviousData,
    }),
  show: ({ organizationId, roleId }: RoleScope) =>
    queryOptions({
      queryKey: roleQueryKeys.show({ organizationId, roleId }),
      queryFn: getRole,
    }),
}

export function useRoles({
  organizationId,
  ...params
}: OrganizationScope & QueryParams<ReturnType<typeof roleQueries.index>> = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useSuspenseQuery({
    ...roleQueries.index({ organizationId: resolvedOrganizationId }),
    ...params,
  })
}

export function useRole({
  organizationId,
  roleId,
  ...params
}: OrganizationScope &
  Pick<RoleScope, 'roleId'> &
  QueryParams<ReturnType<typeof roleQueries.show>> = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useSuspenseQuery({
    ...roleQueries.show({ organizationId: resolvedOrganizationId, roleId }),
    ...params,
  })
}
